import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators';

import { UserSession } from './user-session';
import { UtilService } from './util.service';
import { NativeIntegrationService } from './native-integration.service';

/**
 * The goal of this service is two things:
 *
 * 1. abstract the carefirst response and either continue
 * the observable stream or throw an observable error. This allows
 * other modules using this service as a base to use standard observable
 * callbacks as opposed to a hack checking the response context inside
 * the success callback.
 *
 * 2. abstract the complexity of setting query params. It is a little complex with
 * angular since HttpParams is immutable. Also, abstract converting to javascript
 * object to a FormData object for multipart/form uploads
 *
 * 3. automatically add accountId and timestamps to each service call as query parameters.
 *
 */
@Injectable()
export class CFHttpClient {
  constructor(
    private http: HttpClient,
    private userSession: UserSession,
    private utilService: UtilService,
    private nativeService: NativeIntegrationService
  ) { }

  // to Eric: I feel this is an bad design, which turns the CFHttpClient from stateless into a stateful service
  public static targetURL;

  /**
   * Get request
   *
   * @param url the url to send the request too
   * @param params object containing the query parameters
   */
  get(url, params?, options?): Observable<any> {
    params = {
      ...params,
      accountId: this.userSession.getCurrentAccountId() || '',
      timeStamp: new Date().toISOString()
    };

    params = this.mapRequestData(params, 'params');
    // Adding device type to pass two additional headers to SBS
    options = {
      headers: this.setHeaders()
    };

    return this.http
      .get(this.getAbsoluteURL(url), { params, ...options })
      .pipe(map(this.checkCfResponse));
  }
  
   getLogout(url, params?, options?): Observable<any> {
    params = {
      ...params,
      accountId: this.userSession.getCurrentAccountId() || '',
      timeStamp: new Date().toISOString()
    };

    params = this.mapRequestData(params, 'params');
    // Adding device type to pass two additional headers to SBS
    options = {
      headers: this.setHeaders()
    };

    return this.http
      .get(location.origin + url, { params, ...options })
      .pipe(map(this.checkCfResponse));
  }

  /**
   * Post request
   *
   * @param url the url to send the request too
   * @param body object containing body data to send
   * @param httpOptions additional options such as headers
   */
  post(url, body?, httpOptions?): Observable<any> {
    /**
     * Allows application services to send cf-http raw javascript objects.
     * for form-urlencoded cf-http will create the query string
     * for form data cf-http will create the form data object
     */
    if (this.hasHeader(httpOptions, 'Content-Type', 'application/x-www-form-urlencoded') ||
      this.hasHeader(httpOptions, 'Content-Type', 'application/pdf')) {
      body = this.mapRequestData(body, 'urlencoded').toString();
    } else if (this.hasHeader(httpOptions, 'enctype', 'multipart/form-data')) {
      body = this.mapRequestData(body, 'formdata');
    }
    // httpOptions = {
    //   ...httpOptions,
    //   headers: this.setHeaders()
    // }
    if (url === '/mas/mfa/updateMFAStatus.json' || url === '/mas/sec/insertDeferredPaymentDurationDetails.json') {
      body = this.mapRequestData(body, 'params');
    }
    return this.http
      .post(this.getAbsoluteURL(url), body, httpOptions)
      .pipe(map(this.checkCfResponse));
  }
  postLogout(url, body?, httpOptions?): Observable<any> {
    /**
     * Allows application services to send cf-http raw javascript objects.
     * for form-urlencoded cf-http will create the query string
     * for form data cf-http will create the form data object
     */
    if (this.hasHeader(httpOptions, 'Content-Type', 'application/x-www-form-urlencoded') ||
      this.hasHeader(httpOptions, 'Content-Type', 'application/pdf')) {
      body = this.mapRequestData(body, 'urlencoded').toString();
    } else if (this.hasHeader(httpOptions, 'enctype', 'multipart/form-data')) {
      body = this.mapRequestData(body, 'formdata');
    }
    // httpOptions = {
    //   ...httpOptions,
    //   headers: this.setHeaders()
    // }
    if (url === '/mas/mfa/updateMFAStatus.json' || url === '/mas/sec/insertDeferredPaymentDurationDetails.json') {
      body = this.mapRequestData(body, 'params');
    }
    return this.http
      .post(location.origin + url, body, httpOptions)
      .pipe(map(this.checkCfResponse));
  }



  /**
  * Post request
  * Added Put method for MFA  VerifyToken serice call Need confirmation from Eric
  * @param url the url to send the request too
  * @param body object containing body data to send
  * @param httpOptions additional options such as headers
  */
  put(url, body?, httpOptions?): Observable<any> {
    /**
     * Allows application services to send cf-http raw javascript objects.
     * for form-urlencoded cf-http will create the query string
     * for form data cf-http will create the form data object
     */
    return this.http
      .put(this.getAbsoluteURL(url), body, httpOptions)
      .pipe(map(this.checkCfResponse));
  }

  /**
   * Maps an object to an HttpParams or FormData construct.
   * x-wwww-form-urlencoded also uses the HTTPParams since
   * HttpParams eseentially provides the query string format
   * which is exactaly what x-www-form-urlencoded is looking for.
   *
   * @param params parameters to map
   * @param type the type to map to. params or formdata
   */
  private mapRequestData(data, format: 'formdata' | 'params' | 'urlencoded' | 'json') {
    let mappedData;

    switch (format) {
      case 'formdata':
        mappedData = new FormData();
        break;
      case 'urlencoded':
      case 'params':
        mappedData = new HttpParams();
        break;
      default:
        return data;
    }

    for (const k in data) {
      if (data.hasOwnProperty(k)) {
        // funky code block here because FormData's set method
        // is muttable and returns undefined where HttpParams
        // is imutable and returns a new object with the
        // added/updated param.
        mappedData = mappedData.set(k, data[k]) || mappedData;
      }
    }

    return mappedData;
  }

  /**
   * CF Response always returns
   * 200 status so we have to
   * check the success value
   */
  private checkCfResponse(res) {
    if (res.responseContext) {
      if (!res.responseContext.success) {
        throw res;
      }
    }
    return res;
  }

  private hasHeader(httpOptions, header, value) {
    if (httpOptions && httpOptions.headers) {
      return httpOptions.headers.get(header) === value;
    } else {
      return false;
    }
  }

  public getAbsoluteURL(url: string) {

    let baseUrl;
    if (CFHttpClient.targetURL === undefined) {
      const nativeOS = this.utilService.getNativeOS();
      if (nativeOS !== 'web') {
        this.nativeService.invokeNativeService(nativeOS, 'checkTargetLink', null);
        baseUrl = this.nativeService.getTargetURL();
      }
      else if (window.location.hostname.toString().indexOf('idp') !== -1) {
        if (window.location.hostname.toString() == 'idp.carefirst.com') {
          baseUrl = 'https://apix.carefirst.com';
        }
        else if (window.location.hostname.toString().indexOf('sita') !== -1) {
          baseUrl = 'https://apix-pit.carefirst.com';
        }
        else if (window.location.hostname.toString().indexOf('pit') !== -1) {
          baseUrl = 'https://apix-pit.carefirst.com';
        } else {
          baseUrl = 'https://apix-pit.carefirst.com';
        }
      }
      else if (window.location.hostname.toString().indexOf('member') !== -1) {
        if (window.location.hostname.toString() == 'member.carefirst.com') {
          baseUrl = 'https://apix.carefirst.com';
        }
        else if (window.location.hostname.toString().indexOf('sit') !== -1) {
          baseUrl = 'https://apix-sita.carefirst.com';
        }
        else if (window.location.hostname.toString().indexOf('dev') !== -1) {
          baseUrl = 'https://apix-deva.carefirst.com';
        }
        else if (window.location.hostname.toString().indexOf('tt') !== -1) {
          baseUrl = 'https://apix-pit.carefirst.com';
        }
        else if (window.location.hostname.toString().indexOf('uat') !== -1) {
          baseUrl = 'https://apix-uat.carefirst.com';
        }
        else {
          baseUrl = 'https://apix-deva.carefirst.com';
          //baseUrl = 'https://api-pit.carefirst.com';
          
        }
      } else {
        baseUrl = 'https://apix-deva.carefirst.com';
        //baseUrl = 'https://api-pit.carefirst.com';
      }
      CFHttpClient.targetURL = baseUrl;
    }
    // if the url is already absolute just return it
    if (url.includes('http')) {
      return url;
    } else {
      return CFHttpClient.targetURL + url;
    }
  }

  /**
   * SBS is looking for some additional headers
   * See SBS code:
   * MemberWeb/src/main/com/carefirst/member/present/filters/MemberAuthenticationFilter.java
   */
  protected setHeaders(existingHeaders?) {
    return new HttpHeaders()
      .set('nativeOS', this.utilService.getNativeOS())
      .set('device', this.utilService.getDeviceType());
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/cf-http-client.ts